import React, {useEffect, useRef, useState} from 'react';
import {PaymentRequest} from '@stripe/stripe-js';
import CustomPaymentRequestButton from '../../components/CustomPaymentRequestButton';
import useViewportBottomInset from './useViewportBottomInset';
import {SOCIAL_PROOF_REVIEWS, SocialProofReview} from './reviews';
import './SocialProofBelowFold.css';

// Reused as-is from the existing paywalls — no new assets ship with I36.
import editorsChoiceBadge from '../social-proof/assets/editors-choice.png';
import fiveStarReviewsBadge from '../social-proof/assets/five-star-reviews.png';
import ribbonIcon from '../social-proof/assets/ribbon.svg';
import hbrLogo from '../social-proof/assets/hbr.svg';
import sleepFoundationLogo from '../social-proof/assets/sleep-foundation-logo.svg';
import espnLogo from '../../projects/emailConfirmation-0424/assets/espn@3x.png';
import nytLogo from '../../projects/emailConfirmation-0424/assets/nyt@3x.png';
import forbesLogo from '../email-campaign/assets/Forbes.png';

/**
 * `quiet` — award laurels + press logos only. No numeric claim, no reviews. This is the
 *   arm card I27 originally proposed, and the one that carries no unresolved copy risk.
 * `full` — laurels, the user-count headline, the auto-scrolling review strip, press logos and
 *   the money-back guarantee, plus a sticky CTA.
 */
export type SocialProofBelowFoldVariant = 'quiet' | 'full';

/**
 * The sticky bar is a real purchase control or it does not exist — see the note on
 * {@link SocialProofBelowFold}. The screen only passes this when the paywall's live CTA is
 * our own wallet button.
 */
export type StickyCta = {
  /** Same label as the live CTA, passed down rather than re-derived, so they cannot drift. */
  text: string;
  /** Terms under the button. ReactNode because the product's `pricingText` may be an element. */
  subtext?: React.ReactNode;
  /** True once the live CTA has scrolled out of view. */
  visible: boolean;
  /** The same Stripe payment request the live CTA holds, so this button opens the same sheet. */
  paymentRequest?: PaymentRequest;
  /** Analytics only — the button opens the wallet sheet itself. */
  onClick: () => void;
};

export type SocialProofBelowFoldProps = {
  variant: SocialProofBelowFoldVariant;
  /** Scrolls back to the plan grid / CTA. `source` says which affordance was used. */
  onReturnToPlans: (source: 'back_to_plans') => void;
  /** Omit to render no sticky bar; the block then ends with "Back to plans" instead. */
  stickyCta?: StickyCta;
  /** Ref callback from usePaywallScrollDepth, so we can tell who actually reached this. */
  sectionRef?: (node: HTMLElement | null) => void;
};

const FiveStars = () => (
  <span className="i36-stars" aria-label="5 out of 5 stars">
    {[0, 1, 2, 3, 4].map(i => (
      <svg key={i} width="12" height="12" viewBox="0 0 14 14" fill="#FF9500" aria-hidden="true">
        <path d="M7 .5l1.76 4.57 4.96.29-3.83 3.07 1.29 4.81L7 10.7l-4.18 2.54 1.29-4.81L.28 5.36l4.96-.29z" />
      </svg>
    ))}
  </span>
);

const ReviewCard = ({review}: {review: SocialProofReview}) => (
  <div className="i36-review-card">
    <div className="i36-review-header">
      <p className="i36-review-title">{review.title}</p>
      <div className="i36-review-photo">
        <img src={review.photo} alt="" />
      </div>
    </div>
    <div className="i36-review-meta">
      <FiveStars />
      <span className="i36-review-author">{review.author}</span>
    </div>
    <p className="i36-review-text">{review.text}</p>
  </div>
);

const PressLogos = () => (
  <div className="i36-featured">
    <h2>Featured in</h2>
    <div className="i36-logos">
      <div className="i36-logo-row">
        <div className="i36-logo-pill">
          <img src={espnLogo} alt="ESPN" />
        </div>
        <div className="i36-logo-pill wide">
          <img src={nytLogo} alt="The New York Times" />
        </div>
      </div>
      <div className="i36-logo-row">
        <div className="i36-logo-pill tall">
          <img src={hbrLogo} alt="Harvard Business Review" />
        </div>
        <div className="i36-logo-pill">
          <img src={forbesLogo} alt="Forbes" />
        </div>
        <div className="i36-logo-pill tall">
          <img src={sleepFoundationLogo} alt="Sleep Foundation" />
        </div>
      </div>
    </div>
  </div>
);

/**
 * The I36 credibility block, rendered *after* the checkout content so it lands below the
 * fold. Both arms leave everything above the CTA untouched — that is the whole point of
 * the card, and the reason this is a sibling element rather than an edit to the panel.
 *
 * The April 2026 interstitial put this same material in FRONT of the plan grid and lost
 * 7.7% of non-student and 9.4% of student trial starts to the extra step. Here there is no
 * step: a user who never scrolls sees byte-identical production.
 *
 * On the sticky bar: it is a real purchase control that opens the wallet sheet, or it is
 * absent. Two rules keep it honest, both learned from looking at it on a device:
 *
 *  - It appears only once the live CTA has actually scrolled out of view, not at a guessed
 *    scroll offset. Otherwise there is a window where two purchase buttons are on screen at
 *    once, which reads as two different offers.
 *  - It renders the same CustomPaymentRequestButton the paywall itself renders, holding the
 *    same Stripe payment request. Not a lookalike: styling it by hand meant it drifted from
 *    the real CTA the moment the wallet-CTA flag changed the real one's appearance. Sharing
 *    the component makes them identical by construction and lets this button open the wallet
 *    sheet directly, with no click forwarding and no user-activation hop to lose.
 *  - Its label is handed down from the same value the live CTA uses, and the screen only
 *    supplies it when that CTA is our own wallet button. PaymentIntentScreen also renders
 *    Stripe's own branded button and a credit-card submit, and a bar captioned
 *    "TRY FOR $0.00" beside either of those is simply wrong. In those states the block ends
 *    with the quiet "Back to plans" control instead.
 */
const SocialProofBelowFold = ({variant, onReturnToPlans, stickyCta, sectionRef}: SocialProofBelowFoldProps) => {
  const isFull = variant === 'full';

  // Same iOS Safari problem the chevron has: a bar pinned to bottom: 0 sits behind the
  // browser's own toolbar. See useViewportBottomInset.
  const bottomInset = useViewportBottomInset();

  // The review marquee runs only while it is actually on screen. The original interstitial
  // was the whole page so it could animate unconditionally; here the strip sits below the
  // fold, and most users never scroll to it — animating it the entire time they read the
  // paywall would burn battery to move something nobody is looking at.
  const reviewsRef = useRef<HTMLDivElement>(null);
  const [reviewsOnScreen, setReviewsOnScreen] = useState(false);

  // Tap to stop the strip so a review can be read, tap again to let it go. Deliberately a
  // toggle rather than a CSS :hover pause: on a touchscreen :hover latches on tap and stays
  // latched until the user taps elsewhere, so the strip would stop with no way to restart.
  // Desktop keeps the hover pause (gated on a real pointer in the CSS) and ignores this.
  const [reviewsPaused, setReviewsPaused] = useState(false);
  useEffect(() => {
    const node = reviewsRef.current;
    if (!isFull || !node || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(([entry]) => setReviewsOnScreen(entry.isIntersecting), {threshold: 0});
    observer.observe(node);
    return () => observer.disconnect();
  }, [isFull]);

  return (
    <>
      <div
        ref={sectionRef}
        className={stickyCta ? 'i36-below-fold i36-has-sticky-cta' : 'i36-below-fold'}
        data-variant={variant}
      >
        <div className="i36-badges">
          <img src={editorsChoiceBadge} alt="App Store Editors' Choice, App of the Day" />
          <img src={fiveStarReviewsBadge} alt="68,000+ five star reviews" />
        </div>

        {isFull ? (
          <>
            <div className="i36-title-section">
              {/* The number is the claim on the App Store's first screenshot ("Trusted by
                  5 million early risers") — the most-seen asset on the listing, in the same
                  laurel treatment used directly above, so a user arriving from the App Store
                  meets the same figure twice.

                  The noun is deliberately not the App Store's. This cohort arrives from
                  sleep-debt and energy onboarding, where "early risers" names a goal many of
                  them do not have; "sleeping better" is what they came for. Outcome phrasing
                  with a user count is already published — the US description pairs "Join over
                  10 million users!" with "Become a better sleeper" — so only the number needed
                  substantiating, and 5 million is the conservative one.

                  It replaces an unsourced "11.7M+" inherited from /social-proof. That figure
                  had no citation anywhere; RevenueCat shows lifetime customers crossed 11.70M
                  in late Jan 2026, weeks before /social-proof was built, so it was almost
                  certainly a dashboard reading hard-coded at build time — already stale at
                  12.94M today, and larger than anything marketing has published.

                  Beware: the listing contradicts itself. The US description opens "Join over
                  10 million users!" while GB/CA/AU carry no count at all, and Nyx onboarding
                  (I50) says 500k+. 5 million is the most conservative live claim, so it stays
                  true as the base grows. Reconciling the rest is outside this card. */}
              <h2 className="i36-title">Join 5 million people sleeping better</h2>
              <p className="i36-subtitle">
                Trusted by millions, RISE is your expert companion for better sleep &amp; energy
              </p>
            </div>

            <div className="i36-reviews" ref={reviewsRef} onClick={() => setReviewsPaused(paused => !paused)}>
              <div
                className={[
                  'i36-review-strip',
                  reviewsOnScreen ? 'i36-review-strip-running' : '',
                  reviewsPaused ? 'i36-review-strip-paused' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                {SOCIAL_PROOF_REVIEWS.map(review => (
                  <ReviewCard key={review.author} review={review} />
                ))}
                {/* Second pass so the loop can reset at -50% with no visible seam. */}
                {SOCIAL_PROOF_REVIEWS.map(review => (
                  <ReviewCard key={`repeat-${review.author}`} review={review} />
                ))}
              </div>
            </div>
          </>
        ) : null}

        <PressLogos />

        {isFull ? (
          <div className="i36-guarantee">
            <img src={ribbonIcon} alt="" />
            <h2>100% money-back guarantee</h2>
            <p>
              We're confident RISE will improve your sleep. If we don't meet your expectations within 30 days, we'll
              refund your money.
            </p>
          </div>
        ) : null}

        {/* Without a sticky bar there must still be a way back to the plans. */}
        {stickyCta ? null : (
          <button type="button" className="i36-back-to-plans" onClick={() => onReturnToPlans('back_to_plans')}>
            ↑ Back to plans
          </button>
        )}
      </div>

      {stickyCta ? (
        <div
          className={stickyCta.visible ? 'i36-sticky-cta i36-sticky-cta-visible' : 'i36-sticky-cta'}
          style={{'--i36-bottom-inset': `${bottomInset}px`} as React.CSSProperties}
          aria-hidden={!stickyCta.visible}
        >
          <CustomPaymentRequestButton paymentRequest={stickyCta.paymentRequest} onClick={stickyCta.onClick}>
            {stickyCta.text}
          </CustomPaymentRequestButton>
          {stickyCta.subtext ? <small>{stickyCta.subtext}</small> : null}
        </div>
      ) : null}
    </>
  );
};

export default SocialProofBelowFold;
